import React, { useState, useEffect } from 'react';
import { Users, Circle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const OnlineUsersIndicator = () => {
  const [onlineUsers, setOnlineUsers] = useState({ online_count: 0, users: [] });
  const [showPopup, setShowPopup] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) return;

    const fetchOnlineUsers = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/users/online/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOnlineUsers(response.data);
      } catch (error) {
        console.error('Error fetching online users:', error);
      }
    };

    // Fetch immediately
    fetchOnlineUsers();

    // Update every 30 seconds
    const interval = setInterval(fetchOnlineUsers, 30000);

    return () => clearInterval(interval);
  }, [token]);

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-700',
      driver: 'bg-blue-100 text-blue-700',
      mechanic: 'bg-orange-100 text-orange-700'
    };
    return colors[role] || 'bg-gray-100 text-gray-700';
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Админ',
      driver: 'Водитель',
      mechanic: 'Механик'
    };
    return labels[role] || role;
  };

  if (onlineUsers.online_count === 0) return null;

  return (
    <div className="relative">
      {/* Indicator Button */}
      <button
        onClick={() => setShowPopup(!showPopup)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors relative"
        title={`${onlineUsers.online_count} пользователей онлайн`}
      >
        <Users className="w-5 h-5 text-gray-600" />
        {onlineUsers.online_count > 0 && (
          <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {onlineUsers.online_count}
          </span>
        )}
      </button>

      {/* Popup */}
      {showPopup && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowPopup(false)}
          />

          {/* Popup content */}
          <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-2xl border-2 border-gray-200 z-50 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-3">
              <div className="flex items-center gap-2">
                <Circle className="w-3 h-3 fill-current animate-pulse" />
                <span className="font-bold">
                  Онлайн ({onlineUsers.online_count})
                </span>
              </div>
            </div>

            {/* Users list */}
            <div className="max-h-96 overflow-y-auto">
              {onlineUsers.users.map((user) => (
                <div
                  key={user.username}
                  className="px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Circle className="w-2 h-2 fill-green-500 text-green-500" />
                      <span className="font-medium text-gray-800">
                        {user.username}
                      </span>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                      {getRoleLabel(user.role)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="px-4 py-2 bg-gray-50 text-center">
              <p className="text-xs text-gray-500">
                Обновляется каждые 30 сек
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OnlineUsersIndicator;
